<template>
	<div id="qualifiedDividendIncomeTable">
		<div class="container scroll-fade" :class="{ '--show-gradient': showGradient }">
			<TableFixedLeft @willScroll="(result) => (showGradient = result)">
				<TableStickyHeader :headerHeight="headerHeight" headerBgColor="#f5f5f5">
					<table class="qdi-table">
						<thead>
							<tr>
								<th scope="col" class="fund-name-column" :class="showYearToDateEstimates ? 'lg-5' : 'lg-7'">
									<button
										class="button--simple"
										@click="sortColumn('fundName')"
										ref="fundNameColumn"
										:aria-label="`${tableHeaderContent.labels.fundName} ascending`"
										aria-live="polite"
									>
										<span class="eyebrow no-wrap">{{ tableHeaderContent.labels.fundName }}</span>
										<span
											class="icon"
											:class="sortableCols.fundName.currentSortState === 'ascending' ? 'icon-down' : 'icon-up'"
											aria-hidden="true"
										></span>
									</button>
								</th>
								<th scope="col" class="symbol-column" :class="showYearToDateEstimates ? 'lg-1' : 'lg-2'">
									<span class="colgroup-start" />
									<button
										class="button--simple"
										@click="sortColumn('symbol')"
										ref="symbolColumn"
										:aria-label="`${tableHeaderContent.labels.symbol} ascending`"
										aria-live="polite"
									>
										<span class="eyebrow no-wrap">{{ tableHeaderContent.labels.symbol }}</span>
										<span
											class="icon"
											:class="sortableCols.symbol.currentSortState === 'ascending' ? 'icon-down' : 'icon-up'"
											aria-hidden="true"
										></span>
									</button>
								</th>
								<th v-if="showYearToDateEstimates" scope="col" class="qdi-data-column lg-4">
									<span class="colgroup-start" />
									<span class="eyebrow">
										{{ ytdQdiEstimatesYear }}
										{{ tableHeaderContent.labels.yearToDateQdiEstimates }}
									</span>
								</th>
								<th scope="col" class="qdi-data-column lg-3">
									<span class="colgroup-start" />
									<span class="eyebrow">
										{{ yearEndQdiYear }}
										{{ tableHeaderContent.labels.yearEndQdiFiguresForDividends }}
									</span>
								</th>
								<th scope="col" class="qdi-data-column" :class="showYearToDateEstimates ? 'lg-3' : 'lg-4'">
									<span class="eyebrow">
										{{ yearEndQdiYear }}
										{{ tableHeaderContent.labels.yearEndQdiFiguresForCapitalGains }}
									</span>
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(fund, rowIndex) in paginatedFundData" :key="rowIndex">
								<th v-html="createFundNameLink(fund)" class="fund-name-column"></th>
								<td class="symbol-column">{{ fund.ticker | emDashNull }}</td>
								<td v-if="showYearToDateEstimates" class="qdi-data-column">
									{{ fund.yearToDateQDI | toFixed(2) | append("%") | emDashNull }}
								</td>
								<td class="qdi-data-column">
									{{ fund.priorYearQDIForDividends | toFixed(2) | append("%") | emDashNull }}
								</td>
								<td class="qdi-data-column">
									{{ fund.priorYearQDIForShortTermCapitalGains | toFixed(2) | append("%") | emDashNull }}
								</td>
							</tr>
						</tbody>
					</table>
				</TableStickyHeader>
			</TableFixedLeft>
		</div>
		<Pagination :pageSize="rowCountPerPage" :data="fundDataArray" @pagination:change="updateData" />
	</div>
</template>

<script>
import Pagination from "@vg-faset/ui/src/components/pagination/Pagination.vue";
import Vue2Filters from "vue2-filters";
import TableFixedLeft from "@vg-faset/ui/src/components/table-fixed-left/TableFixedLeft.vue";
import TableStickyHeader from "@vg-faset/ui/src/components/table-sticky-header/TableStickyHeader.vue";
import { emDashNull } from "@vg-faset/core/js/filters/string/index";

export default {
	name: "QualifiedDividendIncomeTable",
	mixins: [Vue2Filters.mixin],
	components: {
		Pagination,
		TableFixedLeft,
		TableStickyHeader
	},
	props: {
		fundData: {
			type: Array,
			default: () => []
		},
		tableHeaderContent: {
			type: Object,
			default: () => ({
				labels: {}
			})
		},
		ytdQdiEstimatesYear: {
			type: String,
			default: ""
		},
		yearEndQdiYear: {
			type: String,
			default: ""
		},
		showYearToDateEstimates: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			fundDataArray: [],
			rowCountPerPage: 10,
			fundTableData: [],
			headerHeight: {
				md: 110,
				lg: 110
			},
			sortableCols: {
				fundName: {
					currentSortState: "ascending",
					propertyToSortBy: "name"
				},
				symbol: {
					currentSortState: "ascending",
					propertyToSortBy: "ticker"
				}
			},
			showGradient: false
		};
	},
	mounted() {
		this.fundDataArray = this.fundData;
	},
	computed: {
		paginatedFundData() {
			return this.fundTableData;
		}
	},
	methods: {
		updateData(filteredRows) {
			this.fundTableData = filteredRows;
		},
		createFundNameLink(fund) {
			let fundNameTableData;

			if (fund.name) {
				let fundIdentifier = fund.ticker ? fund.ticker : fund.fundId;
				fundNameTableData = `<a href="/investments/products/${fundIdentifier}">${fund.name}</a>`;
			}

			return emDashNull(fundNameTableData);
		},
		sortColumn(colName) {
			let sortableCol = this.sortableCols[colName];
			let sortButton = colName === "fundName" ? this.$refs.fundNameColumn : this.$refs.symbolColumn;
			let sortButtonName = sortButton?.textContent.trim();

			if (sortableCol.currentSortState === "ascending") {
				this.fundDataArray = this.orderBy(this.fundDataArray, sortableCol.propertyToSortBy, -1);
				sortableCol.currentSortState = "descending";
				sortButton?.setAttribute("aria-label", sortButtonName + " descending");
			} else {
				this.fundDataArray = this.orderBy(this.fundDataArray, sortableCol.propertyToSortBy);
				sortableCol.currentSortState = "ascending";
				sortButton?.setAttribute("aria-label", sortButtonName + " ascending");
			}
		}
	},
	watch: {
		fundData(newVal) {
			this.fundDataArray = newVal;
		}
	}
};
</script>

<style lang="scss">
#qualifiedDividendIncomeTable {
	padding-bottom: 50px;
}

.qdi-table {
	.button--simple .icon {
		font-size: 8px;
		margin-left: 8px;
		top: -2px;
		position: relative;
		display: inline;
	}

	.colgroup-start {
		height: 110px;
	}

	.eyebrow {
		text-transform: none;
		font-size: 12px;
	}

	td {
		font-size: 15px;
	}

	thead {
		font-weight: $font-weight--bold;
	}

	thead th:first-of-type {
		background-color: $color--neutral-9;
		background-clip: padding-box;
	}

	thead th {
		background-color: $color--neutral-9;
		vertical-align: bottom;
		padding-bottom: 20px;
		font-size: 12px;
		line-height: 18px;
		height: 110px;
		position: relative;
	}

	tbody {
		th {
			a {
				color: $color--black;
				font-weight: bold;
				font-size: 15px;
			}

			a:hover {
				text-decoration: underline;
			}
		}

		tr {
			height: 112px;
		}
	}

	.fund-name-column {
		padding-left: 16px;
		min-width: 168px;
	}

	.symbol-column {
		padding: 20px;
		white-space: nowrap;
	}

	.qdi-data-column {
		padding: 20px;
		min-width: 152px;
	}
}
</style>
